import { useEffect, useState } from "react";
import styles from "./ConsentHistory.module.scss";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";

export default function ConsentHistory() {
  const [records, setRecords] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const currentPage = pagination.page;
  const perPage = pagination.perPage;

  useEffect(() => {
    fetchConsentHistory(currentPage);
  }, [currentPage]);

  const fetchConsentHistory = async (page) => {
    const { consentPreferencesHistoryAPI } = new AfterOnboardAPI();

    try {
      setLoading(true);
      setError(null);

      const response = await consentPreferencesHistoryAPI(page, perPage);

      const history = response?.data?.history || [];
      const paginationData = response?.data?.pagination;

      setRecords(history);

      if (paginationData) {
        setPagination({
          page: paginationData.page,
          perPage: paginationData.perPage,
          total: paginationData.total,
        });
      }
    } catch (error) {
      console.error("Failed to fetch consent history:", error);
      setError("Failed to load consent history.");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.perPage);

  const handlePrevious = () => {
    if (pagination.page > 1) {
      setPagination((prev) => ({
        ...prev,
        page: prev.page - 1,
      }));
    }
  };

  const handleNext = () => {
    if (pagination.page < totalPages) {
      setPagination((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  // Format API date
  const formatDate = (date) => {
    return moment(date).format("DD MMM YYYY, hh:mm A");
  };

  // Group records by month
  const groupedRecords = records.reduce((groups, record) => {
    const month = moment(record.date).format("MMMM YYYY");

    if (!groups[month]) {
      groups[month] = [];
    }

    groups[month].push(record);

    return groups;
  }, {});

  return (
    <div className={`${styles.wrapper} wrapper auto-height`}>
      {/* Header */}
      <div className={styles.header}>
        <button type="button" className={styles.backButton} onClick={() => navigate("/consent-management")}>
          <IoMdArrowBack />
        </button>

        <h1>Consent History</h1>
      </div>

      {/* Content */}
      <main className={styles.content}>
        <h2>Privacy Records</h2>

        <p className={styles.description}>Track your authorizations for data processing, marketing preferences, and service terms.</p>

        {/* Loading */}
        {loading && <div>Loading consent history...</div>}

        {/* Error */}
        {!loading && error && <div>{error}</div>}

        {/* Empty */}
        {!loading && !error && records.length === 0 && <div>No consent history found.</div>}

        {/* Records */}
        {!loading &&
          !error &&
          Object.entries(groupedRecords).map(([month, monthRecords]) => (
            <div key={month}>
              {/* Month */}
              <div className={styles.month}>{month.toUpperCase()}</div>

              {/* Records */}
              <div className={styles.records}>
                {monthRecords.map((record, index) => {
                  const isConsentGiven = Number(record.value) === 1;

                  return <ConsentRecord key={`${record.code}-${record.date}-${index}`} title={record.name} date={formatDate(record.date)} status={isConsentGiven ? "Consent Given" : "Consent Revoked"} type={isConsentGiven ? "success" : "danger"} />;
                })}
              </div>
            </div>
          ))}

        {/* Pagination */}
        {!loading && !error && pagination.total > 0 && (
          <div className={styles.pagination}>
            <button type="button" onClick={handlePrevious} disabled={pagination.page === 1} className={styles.paginationButton}>
              Previous
            </button>

            <span className={styles.paginationInfo}>
              Page {pagination.page} of {totalPages}
            </span>

            <button type="button" onClick={handleNext} disabled={pagination.page === totalPages} className={styles.paginationButton}>
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function ConsentRecord({ title, date, status, type }) {
  const isSuccess = type === "success";

  return (
    <div className={styles.record}>
      <div className={`${styles.statusIcon} ${isSuccess ? styles.statusSuccess : styles.statusDanger}`}>{isSuccess ? "✓" : "⊗"}</div>

      <div className={styles.recordDetails}>
        <div className={styles.recordTitle}>{title}</div>
        <div className={styles.date}>{date}</div>
      </div>

      <div className={`${styles.statusBadge} ${isSuccess ? styles.badgeSuccess : styles.badgeDanger}`}>{status}</div>
    </div>
  );
}
